import React from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { PageNotFound } from "../pages/NotFound";

/**
 * The root route's error boundary.
 *
 * It used to be `<Navigate to="/404" />`. Because `/*` is the root of the whole
 * authenticated app, that turned EVERY uncaught render error anywhere in the
 * portal into a silent redirect to /404 — no stack, no message, and the URL you
 * were on replaced, so the deep link that triggered it was gone before anyone
 * could read it. A crash and a genuine wrong-URL were indistinguishable, and
 * the one report we had of it ("deep links 404, clicking works") could not be
 * diagnosed from the symptom at all.
 *
 * A real 404 response still renders the 404 page. A thrown error now says so:
 * loudly in development, and in production still as the 404 page — but logged,
 * and without discarding the address bar, so the URL that broke is recoverable.
 */
export function RouteErrorBoundary() {
  const error = useRouteError() as any;

  // Always on: this is the only record that the app crashed rather than
  // mis-routed, and it survives the redirect that used to hide it.
  // eslint-disable-next-line no-console
  console.error("[router] uncaught render error", error);

  if (isRouteErrorResponse(error)) return <PageNotFound />;

  if (process.env.NODE_ENV === "production") return <PageNotFound />;

  return (
    <div className="min-h-screen bg-page text-text-1 p-6 flex flex-col gap-3">
      <h1 className="m-0 font-CircularStdBlack" style={{ fontSize: 20 }}>
        Something threw while rendering this page
      </h1>
      <p className="m-0 text-text-2" style={{ fontSize: 13.5 }}>
        This is a crash, not a missing page. It is shown here instead of a 404 so the
        stack and the URL survive. In production this renders the 404 page.
      </p>
      <pre
        className="bg-surface border border-border-token rounded-xl p-4 overflow-auto text-text-2"
        style={{ fontSize: 12, whiteSpace: "pre-wrap", maxHeight: "60vh" }}
      >
        {String(error?.message || error)}
        {error?.stack ? `\n\n${error.stack}` : ""}
      </pre>
    </div>
  );
}

export default RouteErrorBoundary;
